import { definePermissions, type PermissionsConfig, ANYONE_CAN } from '@rocicorp/zero';
import { schema, type Schema } from './zero-schema.gen';

export { schema, type Schema };

// Auth context type that will be passed to permissions
export type AuthContext = {
  userId: string;
  githubId: number;
  installations: Array<{
    id: number;
    account: {
      login: string;
      type: 'User' | 'Organization';
    };
  }>;
};

// Export the actual permission functions for testing
export const allowIfHasOrganizationAccess = (authContext: AuthContext, { cmp }: any) => {
  if (!authContext || !authContext.installations || !Array.isArray(authContext.installations)) {
    return cmp('owner', '!=', 'owner'); // Always false - deny access if no auth context
  }
  return cmp(
    'owner',
    'IN',
    authContext.installations.map((i: any) => i.account.login)
  );
};

export const allowIfHasInstallationAccess = (authContext: AuthContext, { cmp }: any) => {
  if (!authContext || !authContext.installations || !Array.isArray(authContext.installations)) {
    return cmp('installationId', '!=', 'installationId'); // Always false - deny access if no auth context
  }
  return cmp(
    'installationId',
    'IN',
    authContext.installations.map((i: any) => i.id)
  );
};

export const permissions = definePermissions<AuthContext, Schema>(schema, () => {
  return {
    // All repository-level data: users can only access data from orgs they have access to
    PullsTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    PullDiffsTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    CheckSuiteTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    CheckRunsTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    IssueCommentsTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    ReviewCommentsTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    ReactionsTable: {
      row: {
        select: ANYONE_CAN, // Reactions are filtered through comments which have org permissions
        insert: ANYONE_CAN, // Validate in mutators
        update: {
          preMutation: ANYONE_CAN,
          postMutation: ANYONE_CAN,
        },
        delete: ANYONE_CAN,
      },
    },

    ReviewTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    BranchPushesTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    // Organization-level data
    OrgConfigTable: {
      row: {
        select: [allowIfHasOrganizationAccess],
        insert: [allowIfHasOrganizationAccess],
        update: {
          preMutation: [allowIfHasOrganizationAccess],
          postMutation: [allowIfHasOrganizationAccess],
        },
        delete: [allowIfHasOrganizationAccess],
      },
    },

    BackfillStatusTable: {
      row: {
        select: [allowIfHasInstallationAccess],
        insert: [allowIfHasInstallationAccess],
        update: {
          preMutation: [allowIfHasInstallationAccess],
          postMutation: [allowIfHasInstallationAccess],
        },
        delete: [allowIfHasInstallationAccess],
      },
    },

    UserPreferencesTable: {
      row: {
        select: [
          (authContext: AuthContext, { cmp, row }: any) => {
            if (!authContext || !authContext.userId || !authContext.installations || !Array.isArray(authContext.installations)) {
              return cmp('userId', '!=', 'userId'); // Always false - deny access if no auth context
            }
            const orgLogins = authContext.installations.map((i: any) => i.account.login);
            return cmp('userId', authContext.userId).and(cmp('organizationId', 'IN', orgLogins));
          },
        ],
        insert: [
          (authContext: AuthContext, { cmp, row }: any) => {
            if (!authContext || !authContext.userId || !authContext.installations || !Array.isArray(authContext.installations)) {
              return cmp('userId', '!=', 'userId'); // Always false - deny access if no auth context
            }
            const orgLogins = authContext.installations.map((i: any) => i.account.login);
            return cmp('userId', authContext.userId).and(cmp('organizationId', 'IN', orgLogins));
          },
        ],
        update: {
          preMutation: [
            (authContext: AuthContext, { cmp, row }: any) => {
              if (!authContext || !authContext.userId || !authContext.installations || !Array.isArray(authContext.installations)) {
                return cmp('userId', '!=', 'userId'); // Always false - deny access if no auth context
              }
              const orgLogins = authContext.installations.map((i: any) => i.account.login);
              return cmp('userId', authContext.userId).and(cmp('organizationId', 'IN', orgLogins));
            },
          ],
          postMutation: [
            (authContext: AuthContext, { cmp, row }: any) => {
              if (!authContext || !authContext.userId || !authContext.installations || !Array.isArray(authContext.installations)) {
                return cmp('userId', '!=', 'userId'); // Always false - deny access if no auth context
              }
              const orgLogins = authContext.installations.map((i: any) => i.account.login);
              return cmp('userId', authContext.userId).and(cmp('organizationId', 'IN', orgLogins));
            },
          ],
        },
        delete: [
          (authContext: AuthContext, { cmp, row }: any) => {
            if (!authContext || !authContext.userId || !authContext.installations || !Array.isArray(authContext.installations)) {
              return cmp('userId', '!=', 'userId'); // Always false - deny access if no auth context
            }
            const orgLogins = authContext.installations.map((i: any) => i.account.login);
            return cmp('userId', authContext.userId).and(cmp('organizationId', 'IN', orgLogins));
          },
        ],
      },
    },
  } satisfies PermissionsConfig<AuthContext, Schema>;
});
